import {
  QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "shared/api";
import { useConfiguratorOptions } from "../configurator/useConfiguratorOptions";
import {
  ExtraOptionsType,
  OptionTemplate,
  Product,
  Specification,
} from "shared/api/middleware-api-client";
import { toast } from "react-toastify";
import { useCartContext } from "app/providers/CartProvider";
import { useSearchParams } from "react-router-dom";
import { encryptVoucher } from "shared/utils/voucher";
import { findStaander } from "entities/configurator/model/staander";
import { getExtraOptionDisplayName } from "entities/configurator/model/extraOptionDisplay";

export const useShoppingCart = () => {
  const queryClient = useQueryClient();
  const cartContext = useCartContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const { getConfiguratorOptions } = useConfiguratorOptions();

  const fetchShoppingCart = async (shoppingCartId: string) =>
    await queryClient.fetchQuery({
      queryKey: ["shopping-cart"],
      queryFn: async () => (await api().gersGetCart(shoppingCartId)).data,
    });

  const getFetchedShoppingCart = async (shoppingCartId: string) => {
    if (
      !shoppingCartId ||
      queryClient.isFetching({ queryKey: ["shopping-cart"] })
    )
      return;

    try {
      const cart = await fetchShoppingCart(shoppingCartId);

      if (cart.products.length) {
        try {
          const options = await getConfiguratorOptions();

          const cartProductsWithIds = cart.products.map((fetchProduct) => {
            let color: OptionTemplate;
            let doorType: OptionTemplate;
            let doorHandle: OptionTemplate;
            let doorLayout: OptionTemplate;
            let glassType: OptionTemplate;

            options.map((option) => {
              if (option.code === fetchProduct.doorColor.code) color = option;
              else if (option.code === fetchProduct.doorType.code)
                doorType = option;
              else if (option.code === fetchProduct.doorHandle.code)
                doorHandle = option;
              else if (option.code === fetchProduct.doorLayout.code)
                doorLayout = option;
              else if (option.code === fetchProduct.glassType.code)
                glassType = option;
            });

            const extraOptions: ExtraOptionsType[] = [];
            const extraOptionsNames: string[] = [];
            const staanderPosition = findStaander(
              fetchProduct.measurements.widths,
              fetchProduct.measurements.verticalBeams,
              fetchProduct.doorType.code,
            );
            if (fetchProduct.extraOptions) {
              for (const extraOption of fetchProduct.extraOptions) {
                const extra = options.find(
                  (option) => option.code === extraOption.id,
                );

                if (extra) {
                  extraOptions.push({ id: extra.id, amount: extra.amount });
                  extraOptionsNames.push(
                    getExtraOptionDisplayName(
                      extra.code,
                      extra.name,
                      staanderPosition,
                    ),
                  );
                }
              }
            }

            const product: Product = {
              cornerWallIndex: fetchProduct.cornerWallIndex,
              doorColor: color! as Specification,
              doorType: doorType! as Specification,
              doorLayout: doorLayout! as Specification,
              doorHandle: doorHandle! as Specification,
              glassType: glassType! as Specification,
              extraOptions: extraOptions,
              extraOptionsNames: extraOptionsNames,
              measurements: fetchProduct.measurements,
              totalPrice: fetchProduct.totalPrice,
              preset: fetchProduct.preset,
            };

            return product;
          });
          cart.products = cartProductsWithIds;
        } catch (error) {
          toast.error("TODOTEXT: Wrong shopping cart id", {
            toastId: "wrong-shopping-cart-id-toast",
          });
        }
      }

      if (cart.sampleSale) {
        cart.email = "";
        cart.emailAddress = "";
        cart.shoppingCartId = shoppingCartId;
        cart.address.emailAddress = "";
        cart.address.shippingAddress = {
          ...cart.address.shippingAddress,
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
        };
        cart.address.invoiceAddress = {
          ...cart.address.invoiceAddress,
          email: "",
          phone: "",
        };
      }

      if (cart.discountCode) {
        cart.discountCode = encryptVoucher(cart.discountCode);
      }

      cartContext.updateCart(cart);

      localStorage.setItem("shoppingCartId", shoppingCartId);
      searchParams.delete("shoppingCartId");
      setSearchParams(searchParams);

      // TODO: Fix issue where shopping cart update will not trigger state reload in overview component
      window.location.reload();
    } catch (error) {
      toast.error("TODOTEXT: Wrong shopping cart id", {
        toastId: "wrong-shopping-cart-id-toast",
      });
    }
  };

  return { getFetchedShoppingCart };
};
